import useSWR, { SWRConfiguration, SWRResponse, unstable_serialize } from 'swr'
import { GrpcWebClientBase, Metadata } from 'grpc-web'
import * as google_protobuf_wrappers_pb from 'google-protobuf/google/protobuf/wrappers_pb'
import { useContext, useCallback, useMemo } from 'react'
import {
  AuthRecord,
  useAuth,
  useAuthContext,
  useAuthHeader,
} from '../../context/authContext'
import {
  getAuthorizationCacheScope,
  getRpcAccess,
  isPermissionDenied,
  RpcAccessDenied,
  RpcAccess,
} from '../../app/AppAuth/rpcAuthorization'
import { interceptTokenErrors, NTRCE_API_URL } from '../../adapter/gRpcAdapter'
import {
  continuesSession,
  createRpcSession,
  useCurrentAuth,
} from '../../adapter/rpcSession'
import { stringValue } from '../../Utils'
import { AuditContext } from '../../context/AuditContext'
import { getRefererHeader } from '../../adapter/referer'
import { getSwrRequestIdHeader } from '../../adapter/requestIdHeader'
import { GrpcCall, useGetCacheKey } from './utils'
import { queue, QUEUE_PRIORITY } from './queue'
import { useQuerySubscribers } from './querySubscribers'
import {
  RpcQueryCancelled,
  useQueryErrorHandler,
  useRecoverAfterTokenRefresh,
  useRecoverCancelledQuery,
} from './queryCancellation'

export async function callGrpc<Request, Response>(
  asyncCall: GrpcCall<Request, Response>,
  request: Request,
  header: Metadata,
  auth: AuthRecord,
): Promise<Response> {
  if (getRpcAccess(asyncCall, auth, request) !== 'allowed')
    return Promise.reject(new RpcAccessDenied(asyncCall))
  return asyncCall.call(
    {
      client_: new GrpcWebClientBase({ format: 'text' }),
      hostname_: NTRCE_API_URL,
    },
    request,
    {
      ...header,
      ...getSwrRequestIdHeader(header),
    },
  )
}

// For imperative reads/writes outside SWR. Saved callbacks cannot dispatch or
// publish results after their user, account, or permission snapshot changes.
export function useGrpcCall(): <Request, Response>(
  method: GrpcCall<Request, Response>,
  request: Request,
  header: Metadata,
) => Promise<Response> {
  const { authCtx: auth, authDispatch } = useAuthContext()
  const currentAuth = useCurrentAuth(auth)
  return useCallback(
    async <Request, Response>(
      method: GrpcCall<Request, Response>,
      request: Request,
      header: Metadata,
    ) => {
      // A callback saved before a same-scope token refresh acts on the live
      // session, so its recovery starts from the token actually sent.
      if (!continuesSession(auth, currentAuth()))
        throw new RpcAccessDenied(method)
      const session = createRpcSession(
        currentAuth() ?? auth,
        currentAuth,
        method,
        request,
      )
      const response = await interceptTokenErrors(
        session.auth,
        authDispatch,
        (tokenHeader: Metadata) => {
          if (!session.isCurrent()) throw new RpcAccessDenied(method)
          return callGrpc(
            method,
            request,
            { ...header, ...tokenHeader },
            session.auth as AuthRecord,
          ).catch((error: unknown) => {
            if (!session.isCurrent() && !session.canRecover(error as Error))
              throw new RpcAccessDenied(method)
            throw error
          })
        },
        session.acceptRefresh,
      )
      if (!session.isCurrent()) throw new RpcAccessDenied(method)
      return response
    },
    [auth, authDispatch, currentAuth],
  )
}

export type AuthorizedQueryState = { access: RpcAccess; isDisabled: boolean }
export type AuthorizedSwrResponse<Response> = SWRResponse<Response> &
  AuthorizedQueryState

export type SwrGrpcResponse = SWRConfiguration & {
  ignoreAuditContext?: boolean
  persistSize?: boolean
  revalidateFirstPage?: boolean
  headers?: Metadata
  // p-queue priority — see QUEUE_PRIORITY. Set this on calls that gate a page's main
  // content so they aren't stuck behind the rest of the page's fetches.
  priority?: number
}

// Helper hook for executing a GRPC request with the SWR library.
export function useSwrGrpc<Request, Response>(
  asyncCall: GrpcCall<Request, Response>,
  request: Request,
  shouldFetch = true,
  config?: SwrGrpcResponse,
): { response: AuthorizedSwrResponse<Response>; cacheKey: unknown[] } {
  const cacheKey = useGetCacheKey(asyncCall, request)
  const auth = useAuth()
  const access = getRpcAccess(asyncCall, auth, request)
  const enabled = shouldFetch && access === 'allowed'
  const scope = getAuthorizationCacheScope(auth)
  const currentAuth = useCurrentAuth(auth)
  const queryKey = unstable_serialize(enabled ? cacheKey : null)
  // Register before SWR's layout effect can start fetching.
  const hasSubscribers = useQuerySubscribers(queryKey)
  const isActive = useCallback(
    () =>
      hasSubscribers() &&
      !!currentAuth() &&
      getAuthorizationCacheScope(currentAuth()) === scope,
    [currentAuth, scope, hasSubscribers],
  )
  const baseHeader = useAuthHeader()
  const { auditId } = useContext(AuditContext)
  const contextualisedRequest = useMemo(() => {
    if (config?.ignoreAuditContext) {
      return request
    }
    if (!isAuditDependent(request) || !auditId) {
      return request
    }

    // Allow user to set their own audit id and don't override it
    if (request.getAuditId()?.getValue() !== undefined) {
      return request
    }

    return request.setAuditId(stringValue(auditId))
  }, [auditId, config?.ignoreAuditContext, request])

  const headers = useMemo(() => {
    return {
      ...baseHeader,
      ...config?.headers,
      ...getRefererHeader(),
    }
  }, [baseHeader, config?.headers])
  const onError = useQueryErrorHandler(config)

  const response = useSWR<Response>(
    enabled ? cacheKey : null,
    () =>
      queue.add(
        async () => {
          if (!isActive()) throw new RpcQueryCancelled()
          if (
            getRpcAccess(asyncCall, auth, contextualisedRequest) !== 'allowed'
          )
            throw new RpcAccessDenied(asyncCall)
          const data = await callGrpc(
            asyncCall,
            contextualisedRequest,
            headers,
            auth,
          ).catch((error: unknown) => {
            if (!isActive()) throw new RpcQueryCancelled()
            throw error
          })
          if (!isActive()) throw new RpcQueryCancelled()
          return data
        },
        {
          priority: config?.priority ?? QUEUE_PRIORITY.DEFAULT,
        },
      ) as Promise<Response>,
    {
      ...config,
      keepPreviousData: false,
      onError,
      shouldRetryOnError: (error) => {
        if (
          error instanceof RpcQueryCancelled ||
          isPermissionDenied(error) ||
          !isActive()
        )
          return false
        return typeof config?.shouldRetryOnError === 'function'
          ? config.shouldRetryOnError(error)
          : config?.shouldRetryOnError !== false
      },
    },
  )
  const { mutate: swrMutate } = response
  const mutate = useCallback(
    (...args: Parameters<typeof swrMutate>) => {
      if (!enabled || !isActive()) return Promise.resolve(undefined)
      return swrMutate(...args)
    },
    [swrMutate, enabled, isActive],
  ) as typeof swrMutate
  const cancelled = useRecoverCancelledQuery(
    queryKey,
    response.error,
    enabled,
    response.isValidating,
    isActive,
    mutate,
  )
  useRecoverAfterTokenRefresh(
    queryKey,
    response.error,
    enabled,
    response.isValidating,
    auth,
    mutate,
  )
  const guardedResponse = {
    ...response,
    data: enabled ? response.data : undefined,
    // A skipped read is an authorization state, not a failed network request.
    // Keep hook-level error effects and toasts reserved for attempted requests.
    access,
    isDisabled: !shouldFetch || access === 'denied',
    error: enabled && !cancelled ? response.error : undefined,
    isLoading:
      shouldFetch && access === 'pending'
        ? true
        : enabled && (response.isLoading || (cancelled && !response.data)),
    isValidating: enabled && response.isValidating,
    mutate,
  }
  return { response: guardedResponse, cacheKey }
}

type AuditDependent<T> = T & {
  getAuditId(): google_protobuf_wrappers_pb.StringValue | undefined
  setAuditId(value?: google_protobuf_wrappers_pb.StringValue): AuditDependent<T>
}

export function isAuditDependent<T>(p: T): p is AuditDependent<T> {
  if (
    p instanceof Object &&
    !!(p as Record<string, unknown>)['setAuditId'] &&
    !!(p as Record<string, unknown>['getAuditId'])
  ) {
    const request = p as AuditDependent<T>
    return typeof request.getAuditId() !== 'string'
  }

  return false
}
